import type { Channel } from 'stream-chat'

export type ChatUserRole = 'buyer' | 'owner' | 'unknown'

export type NameVisibilityState = 'both_hidden' | 'owner_sees_buyer_only' | 'both_shown'

export type NameRequestState = 'none' | 'pending' | 'rejected'

export type ChatDocState = {
  nameVisibilityState: NameVisibilityState
  buyerRequestedOwnerName: NameRequestState
  ownerRequestedBuyerName: NameRequestState
  lockedBySupport: boolean
}

/** Stream channel id is `chat_${hash}`; Firestore / callable use the hash only. */
export function toChatHash(channelIdOrCid: string): string {
  const raw = channelIdOrCid.includes(':')
    ? (channelIdOrCid.split(':').pop() ?? channelIdOrCid)
    : channelIdOrCid
  return raw.startsWith('chat_') ? raw.slice('chat_'.length) : raw
}

export function toStreamChannelId(channelIdOrCid: string): string {
  const hash = toChatHash(channelIdOrCid)
  return `chat_${hash}`
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

export function getChatUserRole(channel: Channel | null | undefined, streamId: string | null): ChatUserRole {
  if (!channel || !streamId) return 'unknown'
  const data = channel.data as Record<string, unknown> | undefined
  const buyerID = asString(data?.chatBuyerID)
  const ownerID = asString(data?.chatOwnerID)
  if (buyerID && streamId === buyerID) return 'buyer'
  if (ownerID && streamId === ownerID) return 'owner'
  return 'unknown'
}

/** Peer label for channel list (masked until names are written to channel extraData). */
export function getPeerDisplayName(channel: Channel | null | undefined, streamId: string | null): string {
  const role = getChatUserRole(channel, streamId)
  const data = (channel?.data ?? {}) as Record<string, unknown>
  if (role === 'buyer') return asString(data.ownerName) ?? 'Owner'
  if (role === 'owner') return asString(data.buyerName) ?? 'Interested Buyer'
  return asString(data.name) ?? 'Chat'
}

const MASKED_NAME = '✱✱✱✱✱'

/**
 * Conversation header title — matches iOS `CustomChannelHeader`.
 * Uses Firestore visibility; falls back to list-style placeholders when doc is missing.
 */
export function getConversationDisplayName(
  channel: Channel | null | undefined,
  streamId: string | null,
  doc: ChatDocState | null,
): string {
  const role = getChatUserRole(channel, streamId)
  const data = (channel?.data ?? {}) as Record<string, unknown>
  if (!doc) return getPeerDisplayName(channel, streamId)

  if (role === 'buyer') {
    if (doc.nameVisibilityState === 'both_shown') {
      return asString(data.ownerName) ?? 'Owner'
    }
    return MASKED_NAME
  }
  if (role === 'owner') {
    if (
      doc.nameVisibilityState === 'owner_sees_buyer_only' ||
      doc.nameVisibilityState === 'both_shown'
    ) {
      return asString(data.buyerName) ?? 'Buyer'
    }
    return MASKED_NAME
  }
  return MASKED_NAME
}

/** Subtitle under the conversation title (name-reveal status line). */
export function getNameRevealSubtitle(
  role: ChatUserRole,
  doc: ChatDocState | null,
): string | null {
  if (!doc) return 'Request name'
  if (role === 'buyer') {
    if (doc.ownerRequestedBuyerName === 'pending') return 'Owner requested your name'
    if (doc.buyerRequestedOwnerName === 'pending') return 'Name request sent'
    if (doc.buyerRequestedOwnerName === 'rejected') return 'Name request declined'
    if (doc.nameVisibilityState === 'both_shown') return null
    if (doc.nameVisibilityState === 'owner_sees_buyer_only') return 'Owner sees your name'
    return 'Request name'
  }
  if (role === 'owner') {
    if (doc.buyerRequestedOwnerName === 'pending') return 'Buyer requested your name'
    if (doc.ownerRequestedBuyerName === 'pending') return 'Name request sent'
    if (doc.ownerRequestedBuyerName === 'rejected') return 'Name request declined'
    if (doc.nameVisibilityState === 'both_shown') return null
    if (doc.nameVisibilityState === 'owner_sees_buyer_only') return 'Share your name'
    return 'Request name'
  }
  return 'Request name'
}

export function isNameRevealSubtitleTappable(
  role: ChatUserRole,
  doc: ChatDocState | null,
): boolean {
  if (!doc) return true
  if (role === 'buyer') {
    return (
      doc.ownerRequestedBuyerName === 'pending' ||
      doc.buyerRequestedOwnerName === 'rejected' ||
      (doc.buyerRequestedOwnerName === 'none' &&
        !doc.lockedBySupport &&
        doc.nameVisibilityState === 'both_hidden')
    )
  }
  if (role === 'owner') {
    return (
      doc.buyerRequestedOwnerName === 'pending' ||
      doc.ownerRequestedBuyerName === 'rejected' ||
      (doc.nameVisibilityState === 'owner_sees_buyer_only' && !doc.lockedBySupport) ||
      (doc.ownerRequestedBuyerName === 'none' &&
        !doc.lockedBySupport &&
        doc.nameVisibilityState === 'both_hidden')
    )
  }
  return true
}

export function getChannelAddress(channel: Channel | null | undefined): string {
  const data = (channel?.data ?? {}) as Record<string, unknown>
  return asString(data.fullAddress) ?? 'Property Address'
}

export function getChannelBiddleRoute(
  channel: Channel | null | undefined,
): { propertyId: string; biddleId: string } | null {
  const data = (channel?.data ?? {}) as Record<string, unknown>
  const propertyId = asString(data.propertyID)
  const biddleId = asString(data.biddleID)
  if (!propertyId || !biddleId) return null
  return { propertyId, biddleId }
}

/** Preview line for the channel list (iOS: "Start the conversation!" when empty). */
export function getChannelPreviewText(channel: Channel | null | undefined): string {
  const messages = channel?.state?.messages ?? []
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (!m || m.deleted_at || m.type === 'system' || m.silent) continue
    const text = m.text?.trim()
    if (text) return text
    if (m.attachments?.length) return 'Attachment'
  }
  return 'Start the conversation!'
}

export function getChannelActivityDate(channel: Channel | null | undefined): Date | null {
  const lastAt = channel?.state?.last_message_at
  if (lastAt instanceof Date) return lastAt
  if (typeof lastAt === 'string' || typeof lastAt === 'number') {
    const d = new Date(lastAt)
    if (!Number.isNaN(d.getTime())) return d
  }
  const created = channel?.data?.created_at as string | Date | number | undefined
  if (created instanceof Date) return created
  if (typeof created === 'string' || typeof created === 'number') {
    const d = new Date(created)
    if (!Number.isNaN(d.getTime())) return d
  }
  return null
}

/** Messenger-style relative time (today → short time, yesterday, else short date). */
export function formatChannelListTime(date: Date | null): string {
  if (!date) return ''
  const calToday = new Date()
  const startOfToday = new Date(calToday.getFullYear(), calToday.getMonth(), calToday.getDate())
  const startOfYesterday = new Date(startOfToday)
  startOfYesterday.setDate(startOfYesterday.getDate() - 1)
  if (date >= startOfToday) {
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  }
  if (date >= startOfYesterday) return 'Yesterday'
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: '2-digit' })
}

export function channelHasUserMessages(channel: Channel | null | undefined): boolean {
  return getChannelPreviewText(channel) !== 'Start the conversation!'
}

export function parseChatDoc(data: Record<string, unknown> | undefined): ChatDocState {
  const visibility = String(data?.nameVisibilityState ?? 'both_hidden')
  const buyerReq = String(data?.buyerRequestedOwnerName ?? 'none')
  const ownerReq = String(data?.ownerRequestedBuyerName ?? 'none')
  return {
    nameVisibilityState:
      visibility === 'owner_sees_buyer_only' || visibility === 'both_shown'
        ? visibility
        : 'both_hidden',
    buyerRequestedOwnerName:
      buyerReq === 'pending' || buyerReq === 'rejected' ? buyerReq : 'none',
    ownerRequestedBuyerName:
      ownerReq === 'pending' || ownerReq === 'rejected' ? ownerReq : 'none',
    lockedBySupport: Boolean(data?.lockedBySupport),
  }
}

export type NameRevealInteraction =
  | 'makeBuyerRequest'
  | 'makeOwnerRequest'
  | 'respondToBuyerRequest'
  | 'respondToOwnerRequest'
  | 'showBuyerSupportContact'
  | 'showOwnerSupportContact'
  | 'ownerVoluntaryShare'
  | 'none'

export function resolveNameRevealInteraction(
  role: ChatUserRole,
  doc: ChatDocState,
): NameRevealInteraction {
  if (role === 'buyer') {
    if (doc.ownerRequestedBuyerName === 'pending') return 'respondToOwnerRequest'
    if (doc.buyerRequestedOwnerName === 'rejected') return 'showBuyerSupportContact'
    if (
      doc.buyerRequestedOwnerName === 'none' &&
      !doc.lockedBySupport &&
      doc.nameVisibilityState !== 'both_shown'
    ) {
      return 'makeBuyerRequest'
    }
    return 'none'
  }
  if (role === 'owner') {
    if (doc.buyerRequestedOwnerName === 'pending') return 'respondToBuyerRequest'
    if (doc.ownerRequestedBuyerName === 'rejected') return 'showOwnerSupportContact'
    if (doc.nameVisibilityState === 'owner_sees_buyer_only' && !doc.lockedBySupport) {
      return 'ownerVoluntaryShare'
    }
    if (
      doc.ownerRequestedBuyerName === 'none' &&
      !doc.lockedBySupport &&
      doc.nameVisibilityState === 'both_hidden'
    ) {
      return 'makeOwnerRequest'
    }
    return 'none'
  }
  return 'none'
}
